/**
 * auth_utils.js – Authentication / cookie helpers.
 *
 * Pure utilities with no React dependency – safe to import anywhere.
 */

/**
 * Safely parse the twinup auth cookie.
 * Returns {} when the cookie is missing or malformed.
 * @param {string} cookie
 * @returns {Object}
 */
export const parseTwinupCookie = (cookie) => {
    if (!cookie || typeof cookie !== 'string') {
        // Fallback: use the token baked in at build time via .env.local
        const envToken = process.env.REACT_APP_DEV_ACCESS_TOKEN;
        if (envToken) {
            console.log('🔑 Using REACT_APP_DEV_ACCESS_TOKEN from .env.local');
            return { access_token: envToken };
        }
        return {};
    }

    // Case 1: raw JWT token (starts with "eyJ") – wrap it into the expected shape
    if (cookie.trim().startsWith('eyJ')) {
        return { access_token: cookie.trim() };
    }

    // Case 2: JSON-encoded object (normal production format)
    try {
        return JSON.parse(
            cookie
                .replace(/\\054/g, ',')
                .replace(/\\/g, '')
                .replace(/^\"/, '')
                .replace(/\"$/, '')
        );
    } catch (error) {
        console.warn('Unable to parse twinup cookie:', error);
        // Fallback to env token on parse failure
        const envToken = process.env.REACT_APP_DEV_ACCESS_TOKEN;
        if (envToken) return { access_token: envToken };
        return {};
    }
};

/**
 * Ensure an Authorization header has exactly one "Bearer " prefix.
 * @param {string} token
 * @returns {string}
 */
export const formatBearerToken = (token) => {
    if (!token) return '';
    return `Bearer ${token.replace(/^Bearer\s*/i, '')}`;
};

/**
 * Strip the "Bearer " prefix from a token string.
 * @param {string} token
 * @returns {string}
 */
export const extractToken = (token) => {
    if (!token) return '';
    return token.replace(/^Bearer\s*/i, '');
};
